import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SearchableSelect from '../ui/SearchableSelect';
import Button from '../ui/Button';
import Icon from '../Icon';
import { useNotifications } from '../NotificationProvider';
import { loadGuests } from '../../services/GuestService';
import {
  loadGuestRelations, refreshGuestRelations, addGuestRelation, removeGuestRelation, describeGuestRelation,
} from '../../services/GuestRelationsService';
import { loadRelationshipLabels, refreshRelationshipLabelsFromApi } from '../../services/RelationshipLabelsService';
import { readableErrorMessage } from '../../utils/permanentDeleteError';
import type { Guest, GuestRelation } from '../../types/Guests';
import { recordId } from '../../utils/recordId';

interface Props {
  guest: Guest;
  /** Planner and above may change relations; everyone else only reads them. */
  editable: boolean;
}

/** "Father of …" style links between this guest and other guests. Reads and writes the API directly. */
const GuestRelationsPanel: React.FC<Props> = ({ guest, editable }) => {
  const { showError } = useNotifications();
  const [relations, setRelations] = useState<GuestRelation[]>(() => loadGuestRelations(guest.id));
  const [labels, setLabels] = useState(() => loadRelationshipLabels());
  const [relatedId, setRelatedId] = useState('');
  const [label, setLabel] = useState('');
  const [adding, setAdding] = useState(false);

  const refresh = useCallback(() => { refreshGuestRelations(guest.id).then(setRelations).catch(() => { /* keep */ }); }, [guest.id]);
  useEffect(() => {
    refresh();
    refreshRelationshipLabelsFromApi().then(setLabels).catch(() => { /* keep */ });
  }, [refresh]);

  const guestOptions = useMemo(
    () => loadGuests().filter(g => g.id !== guest.id).map(g => ({ value: g.id, label: `${g.name} (${recordId('GS', g.id)})` })),
    [guest.id],
  );
  const labelOptions = useMemo(() => labels.map(l => ({ value: l.label, label: l.label })), [labels]);

  const add = async () => {
    if (!relatedId || !label) return;
    setAdding(true);
    try {
      await addGuestRelation(guest.id, relatedId, label);
      setRelations(loadGuestRelations(guest.id));
      setRelatedId('');
      setLabel('');
    } catch (err) {
      showError(readableErrorMessage(err, 'Could not add this relation. Please try again.'));
    } finally {
      setAdding(false);
    }
  };

  const remove = async (relation: GuestRelation) => {
    try {
      await removeGuestRelation(guest.id, relation.id);
      setRelations(loadGuestRelations(guest.id));
    } catch (err) {
      showError(readableErrorMessage(err, 'Could not remove this relation. Please try again.'));
    }
  };

  return (
    <fieldset className="gst-form__group" data-testid="guest-relations">
      <legend>Related to{relations.length > 0 ? ` (${relations.length})` : ''}</legend>
      {relations.length === 0 && <p className="form-helper">No relations yet.</p>}
      {relations.length > 0 && (
        <ul className="gst-plusones" aria-label="Relations">
          {relations.map(r => (
            <li key={r.id}>
              <span>{describeGuestRelation(r)}</span>
              {editable && (
                <button type="button" className="btn-icon" title="Remove this relation" aria-label={`Remove relation: ${describeGuestRelation(r)}`} onClick={() => remove(r)}>
                  <Icon name="trash" size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {editable && (
        <div className="gst-relations__add">
          <SearchableSelect value={label} options={labelOptions} placeholder="Relationship…" onChange={setLabel} />
          <SearchableSelect value={relatedId} options={guestOptions} placeholder="Select a guest…" onChange={setRelatedId} />
          <Button type="button" variant="outline" size="sm" onClick={add} disabled={!relatedId || !label} loading={adding}>Add</Button>
        </div>
      )}
    </fieldset>
  );
};

export default GuestRelationsPanel;
